import {
  DIAGRAM_OPEN_ZOOM_OPTIONS,
  ICON_PACK_LABELS_UK,
  ICON_PACK_NOTES_UK,
  AUTH_PROVIDER_LABELS_UK,
  normalizeDiagramOpenZoom,
  type IconPackId,
} from '@ukrop-nm/shared';
import { Moon, Sun } from 'lucide-react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useIconPack } from '../icons/IconPackContext';
import { useTheme, type ThemeMode } from '../theme/ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { useUserUiSettings } from '../auth/userSettings';

const THEMES: Array<{ id: ThemeMode; label: string; hint: string; icon: 'sun' | 'moon' }> = [
  { id: 'light', label: 'Світла', hint: 'Світлий інтерфейс за замовчуванням', icon: 'sun' },
  { id: 'dark', label: 'Темна', hint: 'Темний інтерфейс для нічної роботи', icon: 'moon' },
];

export function SettingsPage() {
  const { pack, setPack } = useIconPack();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const { settings, update } = useUserUiSettings();

  return (
    <div className="p-4 md:p-6">
      <h2 className="page-title">Налаштування</h2>
      <p className="page-desc">Акаунт · тема · набори іконок · безпека даних.</p>

      <section className="card mt-4">
        <h3 className="mb-3 text-base font-semibold text-gray-800 dark:text-gray-100">Акаунт</h3>
        {user ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {settings.displayName.trim() || user.name || 'Користувач'}
              </div>
              <div className="text-sm text-gray-500">{user.email || 'без email'}</div>
              <div className="mt-1 text-theme-xs text-gray-400">
                Вхід через{' '}
                {user.provider === 'local'
                  ? 'логін і пароль (Neon)'
                  : user.providers
                      .map((id) =>
                        id === 'local' ? 'логін/пароль' : AUTH_PROVIDER_LABELS_UK[id as keyof typeof AUTH_PROVIDER_LABELS_UK],
                      )
                      .join(', ')}
              </div>
            </div>
            <button type="button" className="btn-secondary" onClick={() => void logout()}>
              Вийти
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-500">
              Увійдіть логіном і паролем — схеми й налаштування збережуться під акаунтом.
            </p>
            <Link to="/login" className="btn-primary">
              Увійти
            </Link>
          </div>
        )}
      </section>

      <section className="card mt-4">
        <h3 className="mb-3 text-base font-semibold text-gray-800 dark:text-gray-100">
          Параметри користувача
        </h3>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">
          Зберігаються в цьому браузері. Після входу — також під акаунтом.
        </p>
        <label className="mb-3 block text-sm text-gray-600 dark:text-gray-300">
          Відображуване імʼя
          <input
            className="focus-ring mt-1.5 h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            value={settings.displayName}
            placeholder={user?.name || 'Як у сервісі входу'}
            onChange={(e) => update({ displayName: e.target.value })}
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={settings.rememberLogin}
            onChange={(e) => update({ rememberLogin: e.target.checked })}
          />
          Запамʼятовувати сесію довше (30 днів)
        </label>
        <div className="field mt-4">
          <label htmlFor="diagram-open-zoom">Масштаб схеми при відкритті</label>
          <select
            id="diagram-open-zoom"
            value={String(settings.diagramOpenZoom)}
            onChange={(e) =>
              update({
                diagramOpenZoom: normalizeDiagramOpenZoom(
                  e.target.value === 'fit' ? 'fit' : Number(e.target.value),
                ),
              })
            }
          >
            {DIAGRAM_OPEN_ZOOM_OPTIONS.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label.replace(/^Відкриття: /, '')}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300">
            Також можна змінити біля кнопок масштабу на самій схемі.
          </p>
        </div>
      </section>

      <div className="notice mt-4">
        Реальні адреси використовуються лише локально для генерації конфігурації та не зберігаються у
        Cloudflare. Офіційні іконки Cisco / Microsoft / Apple з draw.io <strong>не копіюємо</strong>{' '}
        (авторські права). Див. <code>docs/ICON_PACKS.md</code>.
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <section className="card">
          <h3 className="mb-3 text-base font-semibold text-gray-800 dark:text-gray-100">Тема</h3>
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">Оберіть вигляд інтерфейсу. Зберігається з параметрами акаунта.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {THEMES.map((item) => {
              const active = theme === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={clsx(
                    'flex items-start gap-3 rounded-xl border px-3 py-3 text-left transition',
                    active
                      ? 'border-brand-300 bg-brand-25 dark:border-brand-500 dark:bg-brand-500/10'
                      : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600',
                  )}
                  onClick={() => setTheme(item.id)}
                  aria-pressed={active}
                >
                  <span
                    className={clsx(
                      'mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg border',
                      active
                        ? 'border-brand-200 bg-white text-brand-600 dark:border-brand-500/40 dark:bg-gray-950'
                        : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
                    )}
                  >
                    {item.icon === 'sun' ? <Sun size={18} /> : <Moon size={18} />}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-sm text-gray-600 dark:text-gray-300">{item.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="card">
          <h3 className="mb-3 text-base font-semibold text-gray-800 dark:text-gray-100">
            Набір іконок схеми
          </h3>
          <div className="field">
            <label>Стиль відображення</label>
            <select value={pack} onChange={(e) => setPack(e.target.value as IconPackId)}>
              {(Object.keys(ICON_PACK_LABELS_UK) as IconPackId[]).map((id) => (
                <option key={id} value={id}>
                  {ICON_PACK_LABELS_UK[id]}
                </option>
              ))}
            </select>
          </div>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            {(Object.keys(ICON_PACK_NOTES_UK) as IconPackId[]).map((id) => (
              <li key={id}>
                <strong className="text-gray-700 dark:text-gray-200">{ICON_PACK_LABELS_UK[id]}:</strong>{' '}
                {ICON_PACK_NOTES_UK[id]}
              </li>
            ))}
          </ul>
        </section>

        <section className="card lg:col-span-2">
          <h3 className="mb-2 text-base font-semibold text-gray-800 dark:text-gray-100">
            Джерела іконок (огляд)
          </h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li>
              <strong className="text-gray-700 dark:text-gray-200">Дозволено обережно:</strong> власні
              SVG; Lucide (ISC); офіційні Azure/AWS icon packs лише за їхніми умовами (окремий імпорт,
              не зі сторінки draw.io).
            </li>
            <li>
              <strong className="text-gray-700 dark:text-gray-200">Не копіювати:</strong> Cisco
              stencils з draw.io, SF Symbols / Apple product icons, чужі bitmap з чужих сайтів.
            </li>
            <li>
              draw.io бібліотеки (Cisco, Network, AWS, GCP) зручні для ручного малювання в draw.io, але
              для вбудовування у продукт потрібна окрема ліцензійна перевірка кожного пакета.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
